import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";
import {
	Box,
	Button,
	IconButton,
	InputBase,
	Typography,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	TextField,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Stack,
	Tooltip,
	Alert
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { ConfigContext } from "../Config/ConfigContext";
import { UserContext } from "../GlobalContextProviders/UserContext";
import { TenantContext } from "../GlobalContextProviders/TenantContext";
import ErrorAlert from "./ErrorAlert";
import useApi from "../hooks/useApi";
import EnhancedTable from "../EnhancedTable";

/**
 * Helper function to extract error messages from API responses.
 * Supports structured error responses as defined.
 *
 * @param {Object} result - The API response.
 * @returns {string} - The extracted error message.
 */
const extractErrorMessage = (result) => {
	if (result && result.errored) {
		if (result.error && result.error.message) {
			return result.error.message;
		} else if (typeof result.error === "string") {
			return result.error;
		} else {
			return "An unknown error occurred.";
		}
	}
	if (result && result.message) return result.message;
	return "";
};

/**
 * Tenants component loads and manages tenant data.
 *
 * Overview:
 * - In a multi-tenant system, a tenant is a data-segregated workspace that keeps information private and centralized
 *   to one group of people. Users can belong to multiple tenants and switch between them as needed.
 * - Organizations allow multiple users in a specific organization to access and collaborate across a set of tenants.
 *
 * Enhancements:
 * - Link a tenant (TenantsName) to an Organization:
 *   1) Select one of my Organizations to link this tenant (calls PUT /organization/tenant)
 *   2) Share the Tenant Invite Code with an Organization Admin to link from their side
 * - Set default tenant (star icon) using new endpoint
 * - Render tenants using the shared EnhancedTable grid control to keep UI
 *   consistent with the Members screen.
 *
 * @component
 * @example
 * return (
 *   <Tenants />
 * )
 */
export default function Tenants({
	TenantsName = "Workspace",
	TenantsNamePlural = TenantsName + "s"
}) {
	const { config } = useContext(ConfigContext);
	const { user, reloadUser } = useContext(UserContext);
	const {
		tenants,
		currentTenant,
		setTenants,
		reloadTenants,
		searchTenantsByName,
	} = useContext(TenantContext);

	const api = useApi();

	const [formValues, setFormValues] = useState({
		tenantNameFilter: "",
		newOrgName: "",
		config: config,
		addOrgModalIsOpen: false,
		statusType: "",
		statusMessage: "",
	});
	const [formErrors, setFormErrors] = useState({});

	/**
	 * Handles change in form values.
	 *
	 * @param {string} value - The new value.
	 * @param {string} key - The key for the value.
	 */
	const onChange = (value, key) => {
		setFormValues((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	/**
	 * Handles bulk updates to the form values.
	 *
	 * @param {Object} updates - The updates to apply.
	 */
	const onChangeBulk = (updates) => {
		setFormValues((prev) => ({
			...prev,
			...updates,
		}));
	};

	/**
	 * Resets form errors.
	 */
	const onResetFormErrors = () => {
		setFormErrors({});
	};

	/**
	 * Handles invalid form submissions.
	 *
	 * @param {Object} errors - The errors to set.
	 */
	const onInvalidForm = (errors) => {
		setFormErrors(errors);
	};

	/**
	 * Handles failure events.
	 *
	 * @param {string} message - The error message.
	 */
	const onFail = (message) => {
		setFormValues((prev) => ({
			...prev,
			statusType: "error",
			statusMessage: message,
		}));
	};

	const [anchorElUser, setAnchorElUser] = useState(null);

	// State for Edit Tenant Modal
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [tenantToEdit, setTenantToEdit] = useState(null);
	const [editStatus, setEditStatus] = useState("idle"); // 'idle', 'saving', 'success', 'error'
	const [editErrorMessage, setEditErrorMessage] = useState("");

	// State for Link Organization Modal
	const [isLinkOrgModalOpen, setIsLinkOrgModalOpen] = useState(false);
	const [tenantForOrgLink, setTenantForOrgLink] = useState(null);
	const [organizations, setOrganizations] = useState({ rows: [] });
	const [selectedOrgId, setSelectedOrgId] = useState("");
	const [tenantInviteCodeView, setTenantInviteCodeView] = useState("");
	const [orgActionSaving, setOrgActionSaving] = useState(false);

	useEffect(() => {
		// No longer needed due to removal of inline editing
	}, [tenants]);

	/**
	 * Opens the user menu.
	 *
	 * @param {Object} event - The event object.
	 */
	const handleOpenMenu = (event) => {
		setAnchorElUser(event.currentTarget);
	};

	/**
	 * Closes the user menu.
	 */
	const handleCloseMenu = () => {
		setAnchorElUser(null);
	};

	/**
	 * Closes the add modal.
	 *
	 * @param {Object} param0 - Object containing openedState flag.
	 */
	const handleCloseAddModal = ({ openedState = false }) => {
		handleCloseMenu();
		onChange(openedState, "addOrgModalIsOpen");
	};

	/**
	 * Handler to set a tenant as default using the new endpoint.
	 *
	 * @param {string} tenantValue - The tenant ID.
	 */
	const handleSetDefaultTenant = async (tenantValue) => {
		try {
			const result = await api.tenant.setDefault({ tenantId: tenantValue });
			if (result.errored) {
				const errorMessage = extractErrorMessage(result);
				throw new Error(errorMessage || `Failed to set default ${TenantsName.toLowerCase()}.`);
			}
			await reloadUser();
		} catch (error) {
			console.error("Error setting default tenant:", error);
			onChangeBulk({
				statusType: "error",
				statusMessage: error.message || `Failed to update default ${TenantsName.toLowerCase()}.`,
			});
		}
	};

	/**
	 * Submit handler for adding a new organization (Tenant creation).
	 *
	 * @param {Object} params - The parameters.
	 * @param {Object} params.values - The form values.
	 * @param {Function} params.onFail - Failure callback.
	 * @param {Function} params.onInvalidForm - Callback for invalid form.
	 * @param {Function} params.onChangeBulk - Bulk update callback.
	 * @param {Function} params.onResetFormErrors - Reset errors callback.
	 */
	const addOrganizationSubmit = async ({
		values,
		onFail,
		onInvalidForm,
		onChangeBulk,
		onResetFormErrors,
	}) => {
		onChangeBulk({
			statusType: "",
			statusMessage: "",
		});

		let errors = {};

		if (!values.newOrgName)
			errors.newOrgName = "Organization Name must be populated";
		if (!isEmpty(errors)) return onInvalidForm(errors);

		onResetFormErrors();

		try {
			const result = await api.tenant.addOrganization({ orgName: values.newOrgName });
			if (result.errored) {
				const errorMessage = extractErrorMessage(result);
				return onFail(errorMessage);
			}
			await reloadTenants();
			onChangeBulk({
				newOrgName: "",
				addOrgModalIsOpen: false,
				statusType: "success",
				statusMessage: `Successfully saved new Organization: ${values.newOrgName}`,
				_id: result.newOrgId,
			});
		} catch (error) {
			const errorMessage = error.message || "Failed to add organization.";
			onFail(errorMessage);
		}
	};

	/**
	 * Opens the Edit Tenant Modal.
	 *
	 * Ensures the edit field is populated with the tenant name as it exists
	 * at the time the modal is opened (creation of the edit session).
	 *
	 * @param {Object} tenant - The tenant object to edit.
	 */
	const openEditModal = (tenant) => {
		// Capture current name to seed the edit field and retain an original reference
		const seedName = tenant?.name ?? "";
		setTenantToEdit({
			...tenant,
			originalName: seedName,
			name: seedName,
		});
		setIsEditModalOpen(true);
		setEditStatus("idle");
		setEditErrorMessage("");
	};

	/**
	 * Closes the Edit Tenant Modal.
	 */
	const closeEditModal = () => {
		setIsEditModalOpen(false);
		setTenantToEdit(null);
		setEditStatus("idle");
		setEditErrorMessage("");
	};

	/**
	 * Submit handler for editing a tenant's name.
	 *
	 * Ensures we pass tenantId to the API call as required.
	 */
	const editTenantSubmit = async () => {
		if (!tenantToEdit || !tenantToEdit.name) {
			setEditErrorMessage(`${TenantsName} name cannot be empty.`);
			setEditStatus("error");
			return;
		}

		setEditStatus("saving");
		setEditErrorMessage("");

		try {
			const updatePayload = { name: tenantToEdit.name.trim() };

			// Pass tenantId explicitly to align with API expectations
			const result = await api.tenant.update({ id: tenantToEdit._id, data: updatePayload });
			if (result.errored) {
				const errorMessage = extractErrorMessage(result);
				throw new Error(errorMessage || `Failed to update ${TenantsName.toLowerCase()}.`);
			}
			await reloadTenants();
			setEditStatus("success");
			onChangeBulk({
				statusType: "success",
				statusMessage: `Successfully updated ${TenantsName.toLowerCase()} name to: ${tenantToEdit.name}`,
			});

			setTimeout(() => {
				closeEditModal();
			}, 1500);
		} catch (error) {
			console.error("Error updating tenant:", error);
			setEditErrorMessage(error.message || `Failed to update ${TenantsName.toLowerCase()}.`);
			setEditStatus("error");
		}
	};

	/**
	 * Opens the Link Organization modal for a given tenant.
	 *
	 * Loads the user's organizations and sets the candidate tenant.
	 * Displays the tenant's invite code if available (to share with org admins).
	 *
	 * @param {Object} tenant - The tenant object to link.
	 */
	const openLinkOrgModal = async (tenant) => {
		try {
			onChangeBulk({ statusType: "", statusMessage: "" });
			setTenantForOrgLink(tenant);
			setIsLinkOrgModalOpen(true);
			setSelectedOrgId("");
			// Load orgs for the current user
			const orgList = await api.organization.list({ params: {} });
			if (orgList?.errored) {
				throw new Error(extractErrorMessage(orgList));
			}
			setOrganizations(orgList || { rows: [] });
			// Display tenant invite code if the server provided it on the tenant object
			setTenantInviteCodeView(tenant?.orgInviteCode || "");
		} catch (err) {
			onChangeBulk({
				statusType: "error",
				statusMessage: extractErrorMessage(err) || "Failed to load organizations.",
			});
		}
	};

	/**
	 * Closes the Link Organization modal.
	 */
	const closeLinkOrgModal = () => {
		setIsLinkOrgModalOpen(false);
		setTenantForOrgLink(null);
		setSelectedOrgId("");
		setTenantInviteCodeView("");
		setOrgActionSaving(false);
	};

	/**
	 * Links a tenant to the selected organization.
	 */
	const submitLinkOrg = async () => {
		if (!tenantForOrgLink?._id || !selectedOrgId) {
			onChangeBulk({
				statusType: "error",
				statusMessage: "Please select an organization.",
			});
			return;
		}
		try {
			setOrgActionSaving(true);
			const result = await api.organization.addTenant({
				data: { organizationId: selectedOrgId, tenantId: tenantForOrgLink._id }
			});
			if (result?.errored) {
				throw new Error(extractErrorMessage(result));
			}
			onChangeBulk({
				statusType: "success",
				statusMessage: `${TenantsName} linked to organization successfully.`,
			});
			closeLinkOrgModal();
		} catch (err) {
			onChangeBulk({
				statusType: "error",
				statusMessage: extractErrorMessage(err) || `Failed to link ${TenantsName.toLowerCase()} to organization.`,
			});
			setOrgActionSaving(false);
		}
	};

	/**
	 * Copy helper for tenant invite code in the link-org modal.
	 */
	const copyInviteCode = async () => {
		if (!tenantInviteCodeView) return;
		try {
			await navigator.clipboard.writeText(tenantInviteCodeView);
			onChangeBulk({
				statusType: "success",
				statusMessage: `${TenantsName} Invite Code copied to clipboard.`,
			});
		} catch (err) {
			onChangeBulk({
				statusType: "error",
				statusMessage: "Failed to copy invite code.",
			});
		}
	};

	/**
	 * Column definitions for the EnhancedTable grid. These mirror the structure used
	 * in Members and provide a consistent grid experience.
	 *
	 * Columns:
	 * - name: The tenant/workspace name.
	 * - status: Shows whether the tenant is the current active workspace.
	 * - default: Star icon to set/unset default tenant for the user.
	 * - actions: Contextual actions for the tenant (link to org, edit name, switch).
	 */
	const headCells = [
		{ id: "name", label: `${TenantsName} Name` },
		{
			id: "status",
			label: "Status",
			render: ({ rowData }) => {
				const isActive = rowData.isActive;
				return (
					<Stack direction="row" alignItems="center" spacing={1}>
						{isActive && <CheckIcon color="success" fontSize="small" />}
						<Typography variant="body2">
							{isActive ? "Active" : "Available"}
						</Typography>
					</Stack>
				);
			}
		},
		{
			id: "default",
			label: "Default",
			render: ({ rowData }) => {
				const isDefault = rowData.isDefault;
				return (
					<Tooltip title={isDefault ? `Default ${TenantsName}` : `Set as default ${TenantsName.toLowerCase()}`}>
						<IconButton
							aria-label={`set as default ${TenantsName.toLowerCase()}`}
							onClick={() => handleSetDefaultTenant(rowData._id)}
							color={isDefault ? "primary" : "default"}
							size="small"
						>
							{isDefault ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
						</IconButton>
					</Tooltip>
				);
			}
		},
		{
			id: "actions",
			label: "Actions",
			render: ({ rowData }) => {
				const isActive = rowData.isActive;
				return (
					<Stack direction="row" spacing={1} alignItems="center">
						{/*user.signUpType !== "client" && (
							<Tooltip title="Link this workspace to an organization">
								<span>
									<IconButton
										aria-label="link organization"
										onClick={() => openLinkOrgModal(rowData)}
										color="primary"
										size="small"
									>
										<LinkIcon fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						)*/}
						<Tooltip title={`Edit ${TenantsName} name`}>
							<span>
								<IconButton
									aria-label={`edit ${TenantsName.toLowerCase()} name`}
									onClick={() => openEditModal(rowData)}
									color="primary"
									size="small"
								>
									<EditIcon fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Button
							variant={isActive ? "contained" : "outlined"}
							onClick={async () => {
								onChange(rowData._id, "tenantId");
								try {
									await api.tenant.switch({ id: rowData._id });
									window.location.reload();
								} catch (error) {
									const errorMessage =
										extractErrorMessage(error) ||
										"Failed to switch workspace.";
									onChangeBulk({
										statusType: "error",
										statusMessage: errorMessage,
									});
								}
							}}
							disabled={isActive}
							size="small"
						>
							{isActive ? "Active" : "Switch"}
						</Button>
					</Stack>
				);
			}
		}
	];

	/**
	 * Rows for the EnhancedTable. We enrich each tenant row with computed flags:
	 * - isDefault: whether this tenant is the user's default tenant.
	 * - isActive: whether this tenant is the currently selected tenant.
	 *
	 * Additional rule:
	 * - Organization signups should not see the current tenant in the switch list when
	 *   their current tenant matches their user tenant.
	 */
	const filteredTenantRows = (tenants?.rows || []).filter((tenant) => {
		const isOrganizationUser = user?.signUpType === "organization";
		const hasMatchingTenant = currentTenant?._id && user?.tenantId && currentTenant._id === user.tenantId;
		if (isOrganizationUser && hasMatchingTenant && tenant._id === user.tenantId) {
			return false;
		}
		return true;
	});

	const tableRows = filteredTenantRows.map((tenant) => {
		const isDefault = user.defaultTenantId === tenant._id;
		const isActive = currentTenant._id === tenant._id;
		return {
			...tenant,
			isDefault,
			isActive
		};
	});

	return (
		<Box>
			{/* Page Header describing this page */}
			<Box mb={2}>
				<Typography variant="h5">{TenantsNamePlural}</Typography>
				<Alert severity="info" sx={{ mt: 1 }}>
					{`${TenantsNamePlural} are data-segregated workspaces that keep information private and central to one group. Use this page to search, create, rename, switch, and set a default ${TenantsName.toLowerCase()}.`} {`Organizations can link to one or more ${TenantsNamePlural.toLowerCase()} so their members can collaborate across those workspaces as needed.`}
				</Alert>
			</Box>

			{formValues.statusMessage && (
				<Box mb={2}>
					<ErrorAlert
						type={formValues.statusType === "success" ? "success" : "error"}
						message={formValues.statusMessage}
					/>
				</Box>
			)}

			{/* Header controls: search and create */}
			<Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<InputBase
						sx={{ ml: 1, flex: 1 }}
						placeholder={`Search ${TenantsNamePlural.toLowerCase()}`}
						inputProps={{ "aria-label": `search ${TenantsNamePlural.toLowerCase()}` }}
						value={formValues.tenantNameFilter}
						onChange={async (e) => {
							onChange(e.target.value, "tenantNameFilter");
							await searchTenantsByName(e.target.value);
						}}
					/>
					<IconButton type="button" sx={{ p: "10px" }} aria-label="search">
						<SearchIcon />
					</IconButton>
				</Box>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() => handleCloseAddModal({ openedState: true })}
				>
					Create {TenantsName}
				</Button>
			</Box>

			{/* Tenants Grid using shared EnhancedTable for consistency with Members */}
			<EnhancedTable
				tableName={TenantsNamePlural}
				headCells={headCells}
				rows={tableRows}
				refreshFunction={reloadTenants}
				// actionRowClick={(row) => {
				// 	// Default row click: open edit modal for convenience
				// 	openEditModal(row);
				// }}
				actionDelete={(selectedRows) => {
					// Placeholder hook; deletion is not currently supported on tenants here
					// but EnhancedTable expects a handler. We keep it for parity with Members.
					console.log("Delete requested for rows (not implemented):", selectedRows);
				}}
			/>

			{/* Create Organization Modal */}
			<Dialog
				open={formValues.addOrgModalIsOpen}
				onClose={() => handleCloseAddModal({ openedState: false })}
			>
				<DialogTitle>Create Organization</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Create an organization to house all your private data.
					</DialogContentText>
					<TextField
						fullWidth
						margin="normal"
						label="Organization Name"
						value={formValues.newOrgName}
						error={!!formErrors.newOrgName}
						helperText={formErrors.newOrgName}
						onChange={(e) => onChange(e.target.value, "newOrgName")}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleCloseAddModal({ openedState: false })}>
						Cancel
					</Button>
					<Button
						onClick={() =>
							addOrganizationSubmit({
								values: formValues,
								onInvalidForm,
								onResetFormErrors,
								onFail,
								onChangeBulk,
							})
						}
						variant="contained"
					>
						Submit
					</Button>
				</DialogActions>
			</Dialog>

			{/* Edit Tenant Modal */}
			<Dialog open={isEditModalOpen} onClose={closeEditModal}>
				<DialogTitle>Edit {TenantsName} Name</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Update the name of your workspace.
					</DialogContentText>
					<TextField
						fullWidth
						margin="normal"
						label={`${TenantsName} Name`}
						// Ensure the input is seeded with the name captured when the modal opened
						value={tenantToEdit ? (tenantToEdit.name ?? tenantToEdit.originalName ?? "") : ""}
						error={editStatus === "error" && !!editErrorMessage}
						helperText={editStatus === "error" ? editErrorMessage : ""}
						onChange={(e) => {
							setTenantToEdit((prev) => ({
								...prev,
								name: e.target.value,
							}));
						}}
					/>
					{editStatus === "saving" && (
						<Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
							<CircularProgress size={20} />
							<Typography sx={{ ml: 1 }}>Saving...</Typography>
						</Box>
					)}
					{editStatus === "success" && (
						<Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
							<CheckIcon color="success" />
							<Typography sx={{ ml: 1 }}>
								{TenantsName} name updated successfully!
							</Typography>
						</Box>
					)}
					{editStatus === "error" && (
						<Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
							<ErrorIcon color="error" />
							<Typography sx={{ ml: 1 }}>
								{editErrorMessage}
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeEditModal}>Cancel</Button>
					<Button
						onClick={editTenantSubmit}
						variant="contained"
						disabled={editStatus === "saving"}
					>
						Submit
					</Button>
				</DialogActions>
			</Dialog>

			{/* Link {TenantsName} to Organization Modal */}
			<Dialog open={isLinkOrgModalOpen} onClose={closeLinkOrgModal}>
				<DialogTitle>Link Workspace to Organization</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{`Link this ${TenantsName.toLowerCase()} to one of your organizations, or share the ${TenantsName} Invite Code so an Organization Admin can link it from their side.`}
					</DialogContentText>

					{/* Select Organization */}
					<Box sx={{ mt: 2 }}>
						<FormControl fullWidth>
							<InputLabel id="select-org-to-link">Organization</InputLabel>
							<Select
								labelId="select-org-to-link"
								label="Organization"
								value={selectedOrgId}
								onChange={(e) => setSelectedOrgId(e.target.value)}
							>
								{organizations?.rows?.map((o) => (
									<MenuItem key={o._id} value={o._id}>
										{o.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Stack direction="row" spacing={1} sx={{ mt: 1 }}>
							<Button
								variant="contained"
								startIcon={<LinkIcon />}
								onClick={submitLinkOrg}
								disabled={!selectedOrgId || orgActionSaving}
							>
								{orgActionSaving ? "Linking..." : "Link to Organization"}
							</Button>
						</Stack>
					</Box>

					{/* Tenant Invite Code section */}
					<Box sx={{ mt: 3 }}>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							{`${TenantsName} Invite Code`}
						</Typography>
						<Stack direction="row" spacing={1} alignItems="center">
							<TextField
								fullWidth
								label="Invite Code"
								value={tenantInviteCodeView || `Unavailable for this ${TenantsName.toLowerCase()}`}
								InputProps={{ readOnly: true }}
							/>
							<Tooltip title="Copy invite code">
								<span>
									<IconButton
										onClick={copyInviteCode}
										disabled={!tenantInviteCodeView}
										aria-label="copy invite code"
									>
										<ContentCopyIcon />
									</IconButton>
								</span>
							</Tooltip>
						</Stack>
						<Typography variant="caption" color="text.secondary">
							Share this code with an Organization Admin so they can add this workspace to their organization.
						</Typography>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeLinkOrgModal}>Close</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

Tenants.propTypes = {
	TenantsName: PropTypes.string,
	TenantsNamePlural: PropTypes.string
};